<template>
  <div class="toast-stack-container fixed top-16 inset-x-0 z-50 flex flex-col items-center pointer-events-none px-4 gap-2">
    <transition-group name="toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast-item pointer-events-auto flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border shadow-lg text-sm max-w-md w-full transition"
        :class="{
          'toast--info': toast.type === 'info',
          'toast--success': toast.type === 'success',
          'toast--warning': toast.type === 'warning',
          'toast--error': toast.type === 'error',
        }"
      >
        <div class="flex items-center gap-2 min-w-0">
          <i
            class="toast-icon text-base shrink-0"
            :class="{
              'fa-solid fa-spinner fa-spin': toast.text.includes('🔄') || toast.text.includes('Working'),
              'fa-solid fa-circle-check': toast.type === 'success',
              'fa-solid fa-circle-exclamation': toast.type === 'warning',
              'fa-solid fa-circle-xmark': toast.type === 'error',
              'fa-solid fa-circle-info': toast.type === 'info' && !toast.text.includes('🔄') && !toast.text.includes('Working'),
            }"
          ></i>
          <span class="truncate font-medium">{{ toast.text }}</span>
        </div>

        <button
          type="button"
          class="shrink-0 p-1 opacity-70 hover:opacity-100 transition rounded focus:outline-none"
          aria-label="Dismiss notification"
          @click="$emit('dismiss', toast.id)"
        >
          <i class="fa-solid fa-xmark text-xs"></i>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import type { ToastAlert } from '../types';

const props = defineProps<{
  toasts: ToastAlert[];
}>();

const emit = defineEmits<{
  dismiss: [id: string];
}>();

const timers = new Map<string, ReturnType<typeof setTimeout>>();

watch(
  () => props.toasts,
  (newToasts) => {
    const currentIds = new Set(newToasts.map((t) => t.id));

    // Auto-dismiss each toast after 5 seconds
    for (const toast of newToasts) {
      if (!timers.has(toast.id)) {
        const timer = setTimeout(() => {
          emit('dismiss', toast.id);
          timers.delete(toast.id);
        }, 5000);
        timers.set(toast.id, timer);
      }
    }

    // Clean up timer if toast was dismissed early
    for (const [id, timer] of timers.entries()) {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        timers.delete(id);
      }
    }
  },
  { immediate: true, deep: true }
);

onUnmounted(() => {
  for (const timer of timers.values()) {
    clearTimeout(timer);
  }
  timers.clear();
});
</script>

<style scoped>
.toast-enter-active {
  transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-leave-active {
  transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(-16px) scale(0.95);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(-16px) scale(0.92);
}
.toast-move {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-item {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Base Light Themes: High-contrast accessible text and icons */
.toast--info {
  background-color: #eff6ff;
  border-color: #bfdbfe;
  color: #1e3a8a;
}
.toast--info .toast-icon {
  color: #2563eb;
}

.toast--success {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
  color: #14532d;
}
.toast--success .toast-icon {
  color: #16a34a;
}

.toast--warning {
  background-color: #fffbeb;
  border-color: #fde68a;
  color: #78350f;
}
.toast--warning .toast-icon {
  color: #d97706;
}

.toast--error {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #7f1d1d;
}
.toast--error .toast-icon {
  color: #dc2626;
}

/* Dark Themes: High-contrast light text against deep tone background */
:global(:root.dark) .toast--info,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--info {
  background-color: #172554;
  border-color: #1e40af;
  color: #eff6ff;
}
:global(:root.dark) .toast--info .toast-icon,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--info .toast-icon {
  color: #60a5fa;
}

:global(:root.dark) .toast--success,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--success {
  background-color: #052e16;
  border-color: #166534;
  color: #f0fdf4;
}
:global(:root.dark) .toast--success .toast-icon,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--success .toast-icon {
  color: #4ade80;
}

:global(:root.dark) .toast--warning,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--warning {
  background-color: #451a03;
  border-color: #92400e;
  color: #fffbeb;
}
:global(:root.dark) .toast--warning .toast-icon,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--warning .toast-icon {
  color: #fbbf24;
}

:global(:root.dark) .toast--error,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--error {
  background-color: #450a0a;
  border-color: #991b1b;
  color: #fef2f2;
}
:global(:root.dark) .toast--error .toast-icon,
:global(:root:is([data-theme='dark'], [data-theme='aurora'], [data-theme='espresso'], [data-theme='burgundy'], [data-theme='neon-noir'])) .toast--error .toast-icon {
  color: #f87171;
}
</style>
